import * as React from 'react';
import { Box, Flex, Input, Text, VStack } from '@chakra-ui/react';
import { SegmentGroup, ISegmentItem } from '../../../shared/SegmentGroup';
import { BackgroundPresetId, BackgroundType, IScoreboardTheme } from '../types';

const BACKGROUND_TYPE_ITEMS: ISegmentItem[] = [
  { value: 'solid', label: 'Solid' },
  { value: 'gradient', label: 'Gradient' },
  { value: 'image', label: 'Image' },
];

const PRESET_IDS: BackgroundPresetId[] = [
  'stadium-lights',
  'turf-texture',
  'concrete-dark',
  'gradient-blue',
  'gradient-orange',
  'abstract-lines',
];

interface BackgroundSectionProps {
  theme: IScoreboardTheme;
  onUpdate: (updates: Partial<IScoreboardTheme>) => void;
}

export const BackgroundSection = ({
  theme,
  onUpdate,
}: BackgroundSectionProps): React.ReactElement => {
  const [urlError, setUrlError] = React.useState<string | null>(null);

  const handlePresetSelect = (presetId: BackgroundPresetId): void => {
    onUpdate({
      backgroundType: 'image',
      backgroundImage: {
        type: 'preset',
        presetId,
        selectedBackgroundId: null,
        customUrl: null,
        opacity: theme.backgroundImage?.opacity ?? 0.3,
        position: theme.backgroundImage?.position,
        size: theme.backgroundImage?.size,
      },
    });
  };

  const handleOpacityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const opacity = parseFloat(e.target.value);
    onUpdate({
      backgroundImage: {
        ...(theme.backgroundImage || { type: 'custom' }),
        opacity,
      },
    });
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value.trim();
    setUrlError(null);

    // Allow empty value to clear
    if (!value) {
      onUpdate({
        backgroundImage: {
          type: 'custom',
          customUrl: '',
          selectedBackgroundId: null,
          opacity: theme.backgroundImage?.opacity ?? 0.3,
        },
      });
      return;
    }

    // Validate URL format
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:') {
        setUrlError('Only HTTPS URLs are supported.');
        return;
      }
      onUpdate({
        backgroundImage: {
          type: 'custom',
          customUrl: value,
          selectedBackgroundId: null,
          opacity: theme.backgroundImage?.opacity ?? 0.3,
        },
      });
    } catch {
      setUrlError('Invalid URL format.');
      return;
    }
  };

  return (
    <Box role='group' aria-labelledby='background-section-label'>
      <Text
        id='background-section-label'
        fontSize='xs'
        fontWeight={700}
        textTransform='uppercase'
        letterSpacing='wider'
        color='var(--scoreboard-text-secondary, #b0b0b0)'
        mb={3}
      >
        Background
      </Text>
      <SegmentGroup
        value={theme.backgroundType}
        items={BACKGROUND_TYPE_ITEMS}
        onValueChange={(details: { value: string }): void =>
          onUpdate({ backgroundType: details.value as BackgroundType })
        }
        size='sm'
        variant='fullWidth'
        aria-label='Select background type'
      />

      {theme.backgroundType === 'image' && (
        <VStack gap={4} align='stretch' mt={3}>
          {/* Preset Backgrounds */}
          <Box>
            <Text
              id='preset-backgrounds-label'
              fontSize='sm'
              color='var(--scoreboard-text-secondary, #b0b0b0)'
              mb={2}
            >
              Preset Backgrounds
            </Text>
            <Flex gap={2} flexWrap='wrap' role='group' aria-labelledby='preset-backgrounds-label'>
              {PRESET_IDS.map(
                (presetId: BackgroundPresetId): React.ReactElement => (
                  <Box
                    key={presetId}
                    w='70px'
                    h='50px'
                    borderRadius='md'
                    border='2px solid'
                    borderColor={
                      theme.backgroundImage?.type === 'preset' &&
                      theme.backgroundImage?.presetId === presetId
                        ? 'var(--scoreboard-primary, #1a73e8)'
                        : 'rgba(255, 255, 255, 0.2)'
                    }
                    overflow='hidden'
                    cursor='pointer'
                    transition='all 0.2s'
                    _hover={{
                      borderColor: 'var(--scoreboard-primary, #1a73e8)',
                    }}
                    onClick={(): void => handlePresetSelect(presetId)}
                    bg={`url(/assets/scoreboard/backgrounds/${presetId}.jpg)`}
                    bgSize='cover'
                    bgPos='center'
                    role='button'
                    aria-label={`Select ${presetId} background`}
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent): void => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handlePresetSelect(presetId);
                      }
                    }}
                  />
                )
              )}
            </Flex>
          </Box>

          {/* Opacity Control */}
          <Box>
            <Text
              id='opacity-label'
              fontSize='sm'
              color='var(--scoreboard-text-secondary, #b0b0b0)'
              mb={2}
            >
              Image Opacity
            </Text>
            <Flex align='center' gap={2}>
              <Input
                type='range'
                min='0'
                max='1'
                step='0.1'
                value={theme.backgroundImage?.opacity ?? 0.3}
                onChange={handleOpacityChange}
                w='100%'
                aria-labelledby='opacity-label'
                aria-valuemin={0}
                aria-valuemax={1}
                aria-valuenow={theme.backgroundImage?.opacity ?? 0.3}
                aria-valuetext={`${((theme.backgroundImage?.opacity ?? 0.3) * 100).toFixed(0)}%`}
              />
              <Text
                fontSize='sm'
                color='var(--scoreboard-text-secondary, #b0b0b0)'
                minW='40px'
                aria-live='polite'
              >
                {((theme.backgroundImage?.opacity ?? 0.3) * 100).toFixed(0)}%
              </Text>
            </Flex>
          </Box>

          {/* URL Input (fallback) */}
          <Box>
            <Text
              id='custom-url-label'
              fontSize='sm'
              color='var(--scoreboard-text-secondary, #b0b0b0)'
              mb={2}
            >
              Or use a URL
            </Text>
            <Input
              type='text'
              placeholder='https://example.com/background.jpg'
              value={theme.backgroundImage?.customUrl ?? ''}
              onChange={handleUrlChange}
              size='sm'
              bg='rgba(255, 255, 255, 0.05)'
              borderColor={urlError ? 'red.400' : 'rgba(255, 255, 255, 0.2)'}
              color='var(--scoreboard-text-primary, #ffffff)'
              aria-labelledby='custom-url-label'
              aria-invalid={!!urlError}
            />
            {urlError && (
              <Text fontSize='xs' color='red.400' mt={1}>
                {urlError}
              </Text>
            )}
          </Box>
        </VStack>
      )}
    </Box>
  );
};
